import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { AllocationProvider } from './contexts/AllocationContext';
import { usePermissions, PAGE_PERMISSIONS, ISSUER_PERMISSIONS, FACILITY_AGENT_PERMISSIONS, PRIMARY_SUBSCRIBER_PERMISSIONS, CUSTODIAN_PERMISSIONS, INVESTOR_PERMISSIONS, TRUSTEE_PERMISSIONS, SUPER_ADMIN_PERMISSIONS } from './utils/permissionUtils';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';

// Issuer Pages
import IssuerDashboard from './pages/issuer/IssuerDashboard';
import CreateBond from './pages/issuer/CreateBond';
import BondManagement from './pages/issuer/BondManagement';
import BondDetails from './pages/issuer/BondDetails';
import EditBond from './pages/issuer/EditBond';
import BondHoldings from './pages/issuer/BondHoldings';
import BondHolders from './pages/issuer/BondHolders';
// Hidden per request: TokenTransfer page
// import TokenTransfer from './pages/issuer/TokenTransfer';
import TokenPermissions from './pages/issuer/TokenPermissions';
import RedemptionReview from './pages/issuer/RedemptionReview';
import TokenBurn from './pages/issuer/TokenBurn';
import AnnouncementHub from './pages/issuer/AnnouncementHub';
import IssuerAnnouncementDetails from './pages/issuer/AnnouncementDetails';
import ReportingCenter from './pages/issuer/ReportingCenter';
import IssuerAuditLogs from './pages/issuer/AuditLogs';
import AccountSettings from './pages/issuer/AccountSettings';
import IssuerRoleManagement from './pages/issuer/RoleManagement';
// Hidden per request: IssuerWhitelistManagement page
// import IssuerWhitelistManagement from './pages/issuer/WhitelistManagement';
import IssuerDocumentManagement from './pages/issuer/DocumentManagement';
import IssuerBondDocuments from './pages/issuer/BondDocuments';
import IssuerTokenAllocationOutcome from './pages/issuer/TokenAllocationOutcome';

// Facility Agent Pages
import FacilityAgentDashboard from './pages/facility-agent/FacilityAgentDashboard';
import BondApplications from './pages/facility-agent/BondApplications';
import FASubscriptionAllocation from './pages/facility-agent/SubscriptionAllocation';
import SubscriptionManagement from './pages/facility-agent/SubscriptionManagement';
import BidderManagement from './pages/facility-agent/BidderManagement';
import BidderReview from './pages/facility-agent/BidderReview';
import PaymentManagement from './pages/facility-agent/PaymentManagement';
import CouponProfitDistribution from './pages/facility-agent/CouponProfitDistribution';
import RedemptionManagement from './pages/facility-agent/RedemptionManagement';
import SubAgreementManagement from './pages/facility-agent/SubAgreementManagement';
import CreateSubAgreement from './pages/facility-agent/CreateSubAgreement';
import AllocationPreview from './pages/facility-agent/AllocationPreview';
import IssuancePaymentManagement from './pages/facility-agent/IssuancePaymentManagement';
import TokenholderRegistry from './pages/facility-agent/TokenholderRegistry';
import TokenHolderRegister from './pages/facility-agent/TokenHolderRegister';
import EarlyRedemption from './pages/facility-agent/EarlyRedemption';
import MaturityManagement from './pages/facility-agent/MaturityManagement';
import RentasIntegration from './pages/facility-agent/RentasIntegration';
import BondOversight from './pages/facility-agent/BondOversight';
import AdministrativeTasks from './pages/facility-agent/AdministrativeTasks';
import FacilityAgentReporting from './pages/facility-agent/FacilityAgentReporting';
import FacilityAgentSettings from './pages/facility-agent/FacilityAgentSettings';
import FARoleManagement from './pages/facility-agent/RoleManagement';
import FABondHoldings from './pages/facility-agent/FABondHoldings';
import FABondHolders from './pages/facility-agent/FABondHolders';
// Facility Agent announcements removed per new requirements
import FADocumentManagement from './pages/facility-agent/DocumentManagement';
import FABondDocuments from './pages/facility-agent/BondDocuments';

// Custodian Pages
import CustodianDashboard from './pages/custodian/CustodianDashboard';
// Replaced KYCReview with SubInvestors per updated requirements
import SubInvestors from './pages/custodian/SubInvestors';
import CustodianSubInvestorLocRequests from './pages/custodian/SubInvestorLocRequests';
import CustodianWalletManagement from './pages/custodian/WalletManagement';
import CustodianSubscriptionAllocation from './pages/custodian/SubscriptionAllocation';
import CustodianAllocationPreview from './pages/custodian/AllocationPreview';
import PaymentProcessing from './pages/custodian/PaymentProcessing';
import TokenDistribution from './pages/custodian/TokenDistribution';
import InvestorManagement from './pages/custodian/InvestorManagement';
import InterestDistribution from './pages/custodian/InterestDistribution';
import CustodianReporting from './pages/custodian/CustodianReporting';
import CustodianSettings from './pages/custodian/CustodianSettings';
import ClientTransfers from './pages/custodian/ClientTransfers';
import CustodianPayoutManagement from './pages/custodian/PayoutManagement';
import CustodianCouponProfitDistribution from './pages/custodian/CouponProfitDistribution';
import CustodianRoleManagement from './pages/custodian/RoleManagement';
import CustodianBondHoldings from './pages/custodian/BondHoldings';
import CustodianBondHolders from './pages/custodian/BondHolders';
import CustodianAnnouncements from './pages/custodian/Announcements';
import CustodianCouponDistributionRequests from './pages/custodian/CouponDistributionRequests';
import CustodianMaturityManagement from './pages/custodian/MaturityManagement';
import CustodianAnnouncementDetails from './pages/custodian/AnnouncementDetails';
import CustodianBondTokenAllocation from './pages/custodian/BondTokenAllocation';
import CustodianDocumentManagement from './pages/custodian/DocumentManagement';
import CustodianBondDocuments from './pages/custodian/BondDocuments';


// Super Admin Pages
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import SubAccountManagement from './pages/admin/SubAccountManagement';
import RoleManagement from './pages/admin/RoleManagement';
import CreateUser from './pages/admin/CreateUser';
import AdminWalletManagement from './pages/admin/WalletManagement';
import AIAgent from './pages/admin/AIAgent';
import SystemConfig from './pages/admin/SystemConfig';
import SystemMonitor from './pages/admin/SystemMonitor';
import SecurityManagement from './pages/admin/SecurityManagement';
import SuperAdminReporting from './pages/admin/SuperAdminReporting';
import AdminWhitelistManagement from './pages/admin/WhitelistManagement';
import AdminSettings from './pages/admin/AdminSettings';
import AdminDocumentManagement from './pages/admin/DocumentManagement';
import AdminBondDocuments from './pages/admin/BondDocuments';

// Primary Subscriber Pages
import PrimarySubscriberDashboard from './pages/primary-subscriber/PrimarySubscriberDashboard';
import PrimarySubscriberRoleManagement from './pages/primary-subscriber/RoleManagement';
import PrimarySubscriberSubAccountManagement from './pages/primary-subscriber/SubAccountManagement';
import PrimaryTokenHolderRegister from './pages/primary-subscriber/TokenHolderRegister';
import CreateLOC from './pages/primary-subscriber/CreateLOC';
import LOCManagement from './pages/primary-subscriber/LOCManagement';
import SubmitSubAgreement from './pages/primary-subscriber/SubmitSubAgreement';
import PrimarySubscriberBondTokenRequests from './pages/primary-subscriber/BondTokenRequests';
import PrimarySubscriberBondTokenAllocation from './pages/primary-subscriber/BondTokenAllocation';
import PrimarySubscriberBondHoldings from './pages/primary-subscriber/BondHoldings';
import PrimarySubscriberBondHolders from './pages/primary-subscriber/BondHolders';
import PrimarySubscriberSettings from './pages/primary-subscriber/PrimarySubscriberSettings';
import PrimarySubscriberDocumentManagement from './pages/primary-subscriber/DocumentManagement';
import PrimarySubscriberBondDocuments from './pages/primary-subscriber/BondDocuments';

// Investor Pages
import InvestorDashboard from './pages/investor/InvestorDashboard';
import InvestorWalletManagement from './pages/investor/WalletManagement';
import InvestorBondHoldings from './pages/investor/BondHoldings';
import InvestorSubscriptionRequests from './pages/investor/SubscriptionRequests';
import TransactionHistory from './pages/investor/TransactionHistory';
import PayoutManagement from './pages/investor/PayoutManagement';
import InvestorReporting from './pages/investor/InvestorReporting';
import SecondaryTransfers from './pages/investor/SecondaryTransfers';
import InvestorRoleManagement from './pages/investor/RoleManagement';
import InvestorSettings from './pages/investor/InvestorSettings';
import InvestorAnnouncements from './pages/investor/Announcements';
import InvestorCouponDistributionRequests from './pages/investor/CouponDistributionRequests';
import InvestorMaturityManagement from './pages/investor/MaturityManagement';
import InvestorAnnouncementDetails from './pages/investor/AnnouncementDetails';
import BondTokenRequests from './pages/investor/BondTokenRequests';
import InvestorDocumentManagement from './pages/investor/DocumentManagement';
import InvestorBondDocuments from './pages/investor/BondDocuments';

// Trustee Pages
import TrusteeDashboard from './pages/trustee/TrusteeDashboard';
import AssignedBonds from './pages/trustee/AssignedBonds';
import TrusteeBondDetails from './pages/trustee/BondDetails';
import TrusteeBondHolders from './pages/trustee/BondHolders';
import TrusteeDocumentManagement from './pages/trustee/DocumentManagement';
import TrusteeAnnouncements from './pages/trustee/Announcements';
import TrusteeAnnouncementDetails from './pages/trustee/AnnouncementDetails';
import TrusteeRoleManagement from './pages/trustee/RoleManagement';
import TrusteeSubAccountManagement from './pages/trustee/SubAccountManagement';
import TrusteeBondDocuments from './pages/trustee/BondDocuments';

// Import Layout component
import Layout from './components/layout/Layout';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  // Function to map user role to correct URL path
  const getRolePath = (role) => {
    const rolePathMap = {
      'facility_agent': '/facility-agent',
      'primary_subscriber': '/primary-subscriber',
      'issuer': '/issuer',
      'custodian': '/custodian',
      'super_admin': '/admin',
      'investor': '/investor',
      'trustee': '/trustee'
    };
    return rolePathMap[role] || `/${role}`;
  };

  // Function to map user role to settings URL path
  const getSettingsPath = (role) => {
    const settingsPathMap = {
      'facility_agent': '/facility-agent/settings',
      'primary_subscriber': '/primary-subscriber/settings',
      'issuer': '/issuer/settings',
      'custodian': '/custodian/settings',
      'super_admin': '/admin/settings',
      'investor': '/investor/settings',
      'trustee': '/trustee/settings'
    };
    return settingsPathMap[role] || `/${role}/settings`;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user must change password - redirect to settings page
  // Allow access to settings page even if mustChangePassword is true
  if (user.mustChangePassword && !window.location.pathname.includes('/settings')) {
    return <Navigate to={getSettingsPath(user.role)} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Instead of showing unauthorized page, redirect to user's appropriate dashboard
    // This prevents users from seeing unauthorized when clicking back button after role change
    return <Navigate to={getRolePath(user.role)} replace />;
  }

  return children;
};

// Permission-based Route Component
const PermissionRoute = ({ children, requiredPermissions }) => {
  const { user, loading } = useAuth();
  const { canAccessPage } = usePermissions();

  // Function to map user role to settings URL path
  const getSettingsPath = (role) => {
    const settingsPathMap = {
      'facility_agent': '/facility-agent/settings',
      'primary_subscriber': '/primary-subscriber/settings',
      'issuer': '/issuer/settings',
      'custodian': '/custodian/settings',
      'super_admin': '/admin/settings',
      'investor': '/investor/settings',
      'trustee': '/trustee/settings'
    };
    return settingsPathMap[role] || `/${role}/settings`;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user must change password - redirect to settings page
  // Allow access to settings page even if mustChangePassword is true
  if (user.mustChangePassword && !window.location.pathname.includes('/settings')) {
    return <Navigate to={getSettingsPath(user.role)} replace />;
  }

  // Check if user has required permissions for this page
  if (requiredPermissions && !canAccessPage(requiredPermissions)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

function App() {
  const { user, loading } = useAuth();

  // Function to map user role to correct URL path
  const getRolePath = (role) => {
    const rolePathMap = {
      'facility_agent': '/facility-agent',
      'primary_subscriber': '/primary-subscriber',
      'issuer': '/issuer',
      'custodian': '/custodian',
      'super_admin': '/admin',
      'investor': '/investor',
      'trustee': '/trustee'
    };
    const path = rolePathMap[role] || `/${role}`;
    return path;
  };

  // Function to map user role to settings URL path
  const getSettingsPath = (role) => {
    const settingsPathMap = {
      'facility_agent': '/facility-agent/settings',
      'primary_subscriber': '/primary-subscriber/settings',
      'issuer': '/issuer/settings',
      'custodian': '/custodian/settings',
      'super_admin': '/admin/settings',
      'investor': '/investor/settings',
      'trustee': '/trustee/settings'
    };
    return settingsPathMap[role] || `/${role}/settings`;
  };

  // Function to get redirect path after login (settings if mustChangePassword, otherwise dashboard)
  const getLoginRedirectPath = (user) => {
    if (!user) return '/login';
    if (user.mustChangePassword) {
      return getSettingsPath(user.role);
    }
    return getRolePath(user.role);
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="loading..." />;
  }

  return (
    <AllocationProvider>
      <ErrorBoundary>
        <div className="min-h-screen bg-secondary-50">
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
              user ? <Navigate to={getLoginRedirectPath(user)} replace={false} /> : <LoginPage />
            } />
            <Route path="/register" element={
              user ? <Navigate to={getRolePath(user.role)} replace /> : <RegisterPage />
            } />
            <Route path="/forgot-password" element={
              user ? <Navigate to={getRolePath(user.role)} replace /> : <ForgotPasswordPage />
            } />

            {/* Issuer Routes */}
            <Route path="/issuer" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout><IssuerDashboard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/bonds/create" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_CREATE_BOND]}>
                    <CreateBond />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/bonds" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT]}>
                    <BondManagement />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/bonds/:bondId" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT]}>
                    <BondDetails />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/bonds/:bondId/edit" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT]}>
                    <EditBond />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/token-holdings" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT]}>
                    <BondHoldings />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/bond-holders/:bondId" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT]}>
                    <BondHolders />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            {/* /issuer/token-transfers route hidden per request: redirect to dashboard */}
            <Route path="/issuer/token-transfers" element={<Navigate to="/issuer" replace />} />
            <Route path="/issuer/token-permissions" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT]}>
                    <TokenPermissions />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/redemption-review" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT]}>
                    <RedemptionReview />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/token-burn" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT]}>
                    <TokenBurn />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/issuer/announcements" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_ANNOUNCEMENTS]}>
                    <AnnouncementHub />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/announcements/:id" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <PermissionRoute requiredPermissions={[ISSUER_PERMISSIONS.ACCESS_ANNOUNCEMENTS]}>
                    <IssuerAnnouncementDetails />
                  </PermissionRoute>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/audit-logs" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout><IssuerAuditLogs /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/settings" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout><AccountSettings /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/sub-accounts" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <SubAccountManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/roles" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout>
                  <IssuerRoleManagement />
                </Layout>
              </ProtectedRoute>
            } />
            {/* /issuer/whitelist-management route hidden per request: redirect to dashboard */}
            <Route path="/issuer/whitelist-management" element={<Navigate to="/issuer" replace />} />
            <Route path="/issuer/documents" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout><IssuerDocumentManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/bonds/:bondId/documents" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout><IssuerBondDocuments /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/issuer/token-allocation-outcome" element={
              <ProtectedRoute allowedRoles={['issuer']}>
                <Layout><IssuerTokenAllocationOutcome /></Layout>
              </ProtectedRoute>
            } />

            {/* Facility Agent Routes */}
            <Route path="/facility-agent" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><FacilityAgentDashboard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/applications" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/applications']}>
                <Layout><BondApplications /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/subscription" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/subscription']}>
                <Layout><SubscriptionManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/subscription/:subscriptionId/bidders" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><BidderManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/subscription/:subscriptionId/review" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><BidderReview /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/payments" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/payment-management']}>
                <Layout><PaymentManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/coupon-profit-distribution" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/coupon-profit-distribution']}>
                <Layout><CouponProfitDistribution /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/bond-holdings" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/bond-holdings']}>
                <Layout><FABondHoldings /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/bond-holders/:bondId" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/bond-holders/:bondId']}>
                <Layout><FABondHolders /></Layout>
              </PermissionRoute>
            } />
            {/* Hidden per request: Redemption Management */}
            {/* <Route path="/facility-agent/redemption" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/redemption-management']}>
                <Layout><RedemptionManagement /></Layout>
              </PermissionRoute>
            } /> */}
            <Route path="/facility-agent/issuance-payment-management" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/issuance-payment-management']}>
                <Layout><IssuancePaymentManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/create-sub-agreement" element={
              <PermissionRoute requiredPermissions={[FACILITY_AGENT_PERMISSIONS.ACCESS_CREATE_SUB_AGREEMENT]}>
                <Layout><CreateSubAgreement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/sub-agreement-management" element={
              <PermissionRoute requiredPermissions={[FACILITY_AGENT_PERMISSIONS.ACCESS_SUB_AGREEMENT_MANAGEMENT]}>
                <Layout><SubAgreementManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/registry" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/registry']}>
                <Layout><TokenholderRegistry /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/token-holder-register" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/token-holder-register']}>
                <Layout><TokenHolderRegister /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/early-redemption" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><EarlyRedemption /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/maturity-management" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/maturity-management']}>
                <Layout><MaturityManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/rentas" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/facility-agent/rentas-integration']}>
                <Layout><RentasIntegration /></Layout>
              </PermissionRoute>
            } />
            <Route path="/facility-agent/bonds" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><BondOversight /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/admin" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><AdministrativeTasks /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/reports" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><FacilityAgentReporting /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/settings" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><FacilityAgentSettings /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/sub-accounts" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><SubAccountManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/roles" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><FARoleManagement /></Layout>
              </ProtectedRoute>
            } />
            {/* Facility Agent announcements routes removed per new requirements */}
            <Route path="/facility-agent/documents" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><FADocumentManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/bonds/:bondId/documents" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><FABondDocuments /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/facility-agent/token-allocation-outcome" element={
              <ProtectedRoute allowedRoles={['facility_agent']}>
                <Layout><IssuerTokenAllocationOutcome /></Layout>
              </ProtectedRoute>
            } />

            {/* Primary Subscriber Routes */}
            <Route path="/primary-subscriber" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <Layout><PrimarySubscriberDashboard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/roles" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <Layout><PrimarySubscriberRoleManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/sub-accounts" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <Layout><PrimarySubscriberSubAccountManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/token-holder-register" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <PermissionRoute requiredPermissions={[PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_TOKEN_HOLDER]}>
                  <Layout><PrimaryTokenHolderRegister /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/create-loc" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <PermissionRoute requiredPermissions={[PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_CREATE_LOC]}>
                  <Layout><CreateLOC /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/loc-management" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <PermissionRoute requiredPermissions={[PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_LOC_MANAGEMENT]}>
                  <Layout><LOCManagement /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/submit-sub-agreement" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <PermissionRoute requiredPermissions={[PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_SUBMIT_SUB_AGREEMENT]}>
                  <Layout><SubmitSubAgreement /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/bond-token-requests" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <PermissionRoute requiredPermissions={[PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_RECEIVE_BOND_TOKEN]}>
                  <Layout><PrimarySubscriberBondTokenRequests /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/bond-token-allocation" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <PermissionRoute requiredPermissions={[PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_BOND_TOKEN_ALLOCATION]}>
                  <Layout><PrimarySubscriberBondTokenAllocation /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/bond-holdings" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <PermissionRoute requiredPermissions={[PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_BOND_HOLDINGS]}>
                  <Layout><PrimarySubscriberBondHoldings /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/bonds/:bondId/holders" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <PermissionRoute requiredPermissions={[PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_BOND_HOLDINGS]}>
                  <Layout><PrimarySubscriberBondHolders /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/settings" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <Layout><PrimarySubscriberSettings /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/documents" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <Layout><PrimarySubscriberDocumentManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/primary-subscriber/bonds/:bondId/documents" element={
              <ProtectedRoute allowedRoles={['primary_subscriber']}>
                <Layout><PrimarySubscriberBondDocuments /></Layout>
              </ProtectedRoute>
            } />

            {/* Custodian Routes */}
            <Route path="/custodian" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><CustodianDashboard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/sub-investors" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/kyc-review']}>
                <Layout><SubInvestors /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/loc-requests" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/loc-requests']}>
                <Layout><CustodianSubInvestorLocRequests /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/wallets" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/wallet-management']}>
                <Layout><CustodianWalletManagement /></Layout>
              </PermissionRoute>
            } />
            {/* Hidden: Subscription Allocation and Allocation Preview for Custodian */}
            {/* <Route path="/custodian/subscription-allocation" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/subscription-allocation']}>
                <Layout><CustodianSubscriptionAllocation /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/allocation-preview" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/allocation-preview']}>
                <Layout><CustodianAllocationPreview /></Layout>
              </PermissionRoute>
            } /> */}
            <Route path="/custodian/bond-holdings" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/bond-holdings']}>
                <Layout><CustodianBondHoldings /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/bond-token-allocation" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/bond-token-allocation']}>
                <Layout><CustodianBondTokenAllocation /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/bonds/:bondId/holders" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/bond-holders']}>
                <Layout><CustodianBondHolders /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/payments" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><PaymentProcessing /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/tokens" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><TokenDistribution /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/investors" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/investor-management']}>
                <Layout><InvestorManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/distributions" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><InterestDistribution /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/reports" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><CustodianReporting /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/settings" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><CustodianSettings /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/client-transfers" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><ClientTransfers /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/payouts" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><CustodianPayoutManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/coupon-distribution-requests" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><CustodianCouponDistributionRequests /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/coupon-profit-distribution" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/coupon-profit-distribution']}>
                <Layout><CustodianCouponProfitDistribution /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/maturity-management" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/maturity-management']}>
                <Layout><CustodianMaturityManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/sub-accounts" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><SubAccountManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/roles" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><CustodianRoleManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/announcements" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/announcements']}>
                <Layout><CustodianAnnouncements /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/announcements/:id" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/custodian/announcements']}>
                <Layout><CustodianAnnouncementDetails /></Layout>
              </PermissionRoute>
            } />
            <Route path="/custodian/documents" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><CustodianDocumentManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/custodian/bonds/:bondId/documents" element={
              <ProtectedRoute allowedRoles={['custodian']}>
                <Layout><CustodianBondDocuments /></Layout>
              </ProtectedRoute>
            } />


            {/* Super Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout><SuperAdminDashboard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/users']}>
                <Layout><UserManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/sub-accounts" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout><SubAccountManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/roles" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout><RoleManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users/create" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/users/create']}>
                <Layout><CreateUser /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/wallets" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/wallets']}>
                <Layout><AdminWalletManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/ai-agent" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/ai-agent']}>
                <Layout><AIAgent /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/config" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/config']}>
                <Layout><SystemConfig /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/monitor" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/monitor']}>
                <Layout><SystemMonitor /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/security" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/security']}>
                <Layout><SecurityManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/reports" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/reports']}>
                <Layout><SuperAdminReporting /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/whitelist-management" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/whitelist-management']}>
                <Layout><AdminWhitelistManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/onboarding" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/onboarding']}>
                <Layout><UserManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/bond-management" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/bond-management']}>
                <Layout><UserManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/payment-management" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/admin/payment-management']}>
                <Layout><UserManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout><AdminSettings /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/documents" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout><AdminDocumentManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/bonds/:bondId/documents" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Layout><AdminBondDocuments /></Layout>
              </ProtectedRoute>
            } />

            {/* Investor Routes */}
            <Route path="/investor" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <Layout><InvestorDashboard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/investor/wallet" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/wallet']}>
                  <Layout><InvestorWalletManagement /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/investor/holdings" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/holdings']}>
                <Layout><InvestorBondHoldings /></Layout>
              </PermissionRoute>
            } />
            <Route path="/investor/holdings/:id" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/holdings']}>
                <Layout><InvestorBondHoldings /></Layout>
              </PermissionRoute>
            } />
            <Route path="/investor/subscription-requests" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/subscription-requests']}>
                <Layout><InvestorSubscriptionRequests /></Layout>
              </PermissionRoute>
            } />
            <Route path="/investor/transactions" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <Layout><TransactionHistory /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/investor/payouts" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/coupon-distribution-requests']}>
                <Layout><PayoutManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/investor/coupon-distribution-requests" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/coupon-distribution-requests']}>
                <Layout><InvestorCouponDistributionRequests /></Layout>
              </PermissionRoute>
            } />
            <Route path="/investor/maturity-management" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/maturity-management']}>
                <Layout><InvestorMaturityManagement /></Layout>
              </PermissionRoute>
            } />
            <Route path="/investor/reports" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <Layout><InvestorReporting /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/investor/secondary-transfers" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <Layout><SecondaryTransfers /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/investor/sub-accounts" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <Layout><SubAccountManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/investor/roles" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <Layout><InvestorRoleManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/investor/settings" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <Layout><InvestorSettings /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/investor/announcements" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/announcements']}>
                <Layout><InvestorAnnouncements /></Layout>
              </PermissionRoute>
            } />
            <Route path="/investor/announcements/:id" element={
              <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/announcements']}>
                <Layout><InvestorAnnouncementDetails /></Layout>
              </PermissionRoute>
            } />
            <Route path="/investor/bond-token-requests" element={
              <ProtectedRoute allowedRoles={['investor', 'custodian']}>
                <PermissionRoute requiredPermissions={PAGE_PERMISSIONS['/investor/bond-token-requests']}>
                  <Layout><BondTokenRequests /></Layout>
                </PermissionRoute>
              </ProtectedRoute>
            } />
            <Route path="/investor/documents" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <Layout><InvestorDocumentManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/investor/bonds/:bondId/documents" element={
              <ProtectedRoute allowedRoles={['investor']}>
                <Layout><InvestorBondDocuments /></Layout>
              </ProtectedRoute>
            } />

            {/* Trustee Routes */}
            <Route path="/trustee" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeDashboard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/bonds" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><AssignedBonds /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/bonds/:bondId" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeBondDetails /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/bonds/:bondId/holders" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeBondHolders /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/holders" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeBondHolders /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/documents" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeDocumentManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/bonds/:bondId/documents" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeBondDocuments /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/announcements" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeAnnouncements /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/announcements/:id" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeAnnouncementDetails /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/roles" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeRoleManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/trustee/subaccounts" element={
              <ProtectedRoute allowedRoles={['trustee']}>
                <Layout><TrusteeSubAccountManagement /></Layout>
              </ProtectedRoute>
            } />

            {/* Default Routes */}
            <Route path="/" element={
              user ? <Navigate to={getRolePath(user.role)} replace /> : <Navigate to="/login" replace />
            } />

            {/* Unauthorized Route */}
            <Route path="/unauthorized" element={
              <ProtectedRoute allowedRoles={['investor', 'custodian', 'facility_agent', 'issuer', 'super_admin', 'trustee', 'primary_subscriber']}>
                <Layout>
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
                      <div className="mb-6">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-error-100">
                          <svg className="h-6 w-6 text-error-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                      </div>
                      <h1 className="text-2xl font-bold text-error-600 mb-4">Access Denied</h1>
                      <p className="text-secondary-600 mb-6">You don't have permission to access this page. Please use the navigation menu to access pages you have permission to view.</p>
                      <div className="space-y-3">
                        <button
                          onClick={() => window.history.back()}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Go Back
                        </button>
                        <button
                          onClick={() => window.location.href = getRolePath(user?.role || 'investor')}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Go to Dashboard
                        </button>
                      </div>
                    </div>
                  </div>
                </Layout>
              </ProtectedRoute>
            } />

            {/* 404 Route */}
            <Route path="*" element={
              user ? (
                <ProtectedRoute allowedRoles={['investor', 'custodian', 'facility_agent', 'issuer', 'super_admin']}>
                  <Layout>
                    <div className="min-h-screen flex items-center justify-center bg-gray-50">
                      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
                        <div className="mb-6">
                          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-secondary-100">
                            <svg className="h-6 w-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                            </svg>
                          </div>
                        </div>
                        <h1 className="text-2xl font-bold text-secondary-900 mb-4">Page Not Found</h1>
                        <p className="text-secondary-600 mb-6">The page you're looking for doesn't exist.</p>
                        <div className="space-y-3">
                          <button
                            onClick={() => window.history.back()}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Go Back
                          </button>
                          <button
                            onClick={() => window.location.href = getRolePath(user?.role || 'investor')}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Go to Dashboard
                          </button>
                        </div>
                      </div>
                    </div>
                  </Layout>
                </ProtectedRoute>
              ) : (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
                    <div className="mb-6">
                      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-secondary-100">
                        <svg className="h-6 w-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                        </svg>
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold text-secondary-900 mb-4">Page Not Found</h1>
                    <p className="text-secondary-600 mb-6">The page you're looking for doesn't exist.</p>
                    <button
                      onClick={() => window.location.href = '/login'}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Go to Login
                    </button>
                  </div>
                </div>
              )
            } />
          </Routes>
        </div>
      </ErrorBoundary>
    </AllocationProvider>
  );
}

export default App;
